import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantContext } from '@/hooks/useTenantContext';

/**
 * Trading names ("brands") of the current company. A campaign maps to one
 * brand; a lead takes its campaign's brand, else the company default; the
 * technician app shows the lead's brand as "Trading as" on the job.
 */
export interface Brand {
  id: string;
  company_id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
}

const BRAND_COLUMNS = 'id, company_id, name, is_default, is_active';

export function useBrands(activeOnly = false) {
  return useQuery({
    queryKey: ['brands', activeOnly],
    queryFn: async () => {
      let query = supabase
        .from('brands')
        .select(BRAND_COLUMNS)
        .order('is_default', { ascending: false })
        .order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Brand[];
    },
  });
}

export function useBrandMutations() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['brands'] });
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['campaigns-with-costs'] });
    queryClient.invalidateQueries({ queryKey: ['job-lead-info'] });
    queryClient.invalidateQueries({ queryKey: ['job-brand'] });
  };

  const create = useMutation({
    mutationFn: async ({ name, makeDefault }: { name: string; makeDefault: boolean }) => {
      if (!tenantId) throw new Error('No company selected');
      const { error } = await supabase
        .from('brands')
        .insert({ company_id: tenantId, name: name.trim(), is_default: makeDefault });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...patch }: { id: string; name?: string; is_default?: boolean; is_active?: boolean }) => {
      const { error } = await supabase.from('brands').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { create, update };
}
